import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Form, Formik } from 'formik';

import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import PaymentMethods, {
  SubmitButtonVisibilitiesRecord,
} from './PaymentMethods/PaymentMethods';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import ModalPageContent from 'components/Modals/ModalPageContent/ModalPageContent';
import PaymentFieldsError from './PaymentFieldsError/PaymentFieldsError';
import PaymentError from './PaymentError/PaymentError';
import CheckoutSummaryAfter from '../CheckoutSummary/CheckoutSummaryAfter/CheckoutSummaryAfter';
import ButtonWithArrow from 'components/UI/Buttons/ButtonWithArrow/ButtonWithArrow';
import Loading from 'components/Common/Loading/Loading';
import useCartPayment from 'hooks/checkout/useCartPayment';
import { useCustomer } from 'hooks/useCustomer';
import { useModalMultiples } from 'hooks/useModal';
import useCurrentCountry from 'hooks/useCurrentCountry';
import PaymentValidationSchema from 'schemas/PaymentValidationSchema';
import { getUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { getPayPageUri } from 'services/sips';
import { ENV_API_KEY_SYMFONY } from 'settings/env';
import { Cart } from 'types/Cart';

import styles from './PaymentContainer.module.scss';

type Props = {
  cart: Cart;
  paymentMethods: string[];
};

function PaymentContainer({ cart, paymentMethods }: Props): JSX.Element {
  const { t } = useTranslation(['checkout']);
  const { isUk } = useCurrentCountry();
  const { updatePaymentMethod } = useCartPayment();
  const { open, openModal, closeModal } = useModalMultiples();
  const [targetUrl, setTargetUrl] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const { customer } = useCustomer();
  const [submitClicked, setSubmitClicked] = useState<boolean>(false);
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [canFollowPaymentMethod, setCanFollowPaymentMethod] = useState(false);
  const paymentMethod =
    canFollowPaymentMethod && paymentMethods.includes(cart.paymentMethod)
      ? cart.paymentMethod
      : null;

  const [isUpdatePaymentFetching, setUpdatePaymentFetching] = useState<boolean>(
    false
  );

  const [
    submitButtonVisibilities,
    setSubmitButtonVisibilities,
  ] = useState<SubmitButtonVisibilitiesRecord>({});

  const isButtonHidden = Object.values(submitButtonVisibilities).some(
    val => !val
  );

  useEffect(() => {
    const pageFooterEl = document.querySelector('.page-footer');

    pageFooterEl?.classList.add(styles.footerCheckout);

    return () => {
      pageFooterEl?.classList.remove(styles.footerCheckout);
    };
  }, []);

  useEffect(() => {
    if (cart?.paymentMethod && !paymentMethods.includes(cart.paymentMethod)) {
      void setPaymentMethod(undefined); // Resetting the payment method if it is not available
    }
  }, [cart?.paymentMethod, paymentMethods]);

  const setPaymentMethod = useCallback(
    async (paymentMethod?: string, catchAbortSignal = true) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        setUpdatePaymentFetching(true);
        return await updatePaymentMethod(paymentMethod, {
          signal: abortController.signal,
        }).catch(error => {
          if (catchAbortSignal && error.name === 'AbortError') return;
          if (error.name !== 'AbortError') {
            console.error(error);
          }

          return Promise.reject(error);
        });
      } finally {
        setCanFollowPaymentMethod(true);
        setUpdatePaymentFetching(false);
      }
    },
    []
  );

  function handleClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    event.stopPropagation();
    setTargetUrl(event.currentTarget.href);
    openModal('pageContent');
  }

  async function onSubmit({ paymentMethod, cellPhone }) {
    setLoading(true);

    if (isUpdatePaymentFetching || cart.paymentMethod !== paymentMethod) {
      // If isUpdatePaymentFetching is true, it's possible the cart is not updated
      await setPaymentMethod(paymentMethod); // Force to set the payment method before validate order
    }

    if (!customer?.cellPhone && cellPhone) {
      try {
        await fetcherWithBody(apiUrls.getCustomerUpdate(), {
          cellPhone: replacePhoneNumberWithoutSpace(cellPhone),
        });
      } catch (err) {
        // Error during save customer
        console.error(err);
      }
    }

    try {
      const response = await fetch(apiUrls.getOrderValidation(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-AUTH-TOKEN': `${ENV_API_KEY_SYMFONY}`,
        },
      });

      if (!response.ok) {
        setLoading(false);
        const data = await response.json();
        console.error('Order validation failed:', data.message);
        return;
      }

      const content = await response.json();
      if (content.sips) {
        cart && localStorage.setItem('order', JSON.stringify(cart));
        window.location.href = getPayPageUri(content.evtId);
      } else if (content.evtId) {
        cart && localStorage.setItem('order', JSON.stringify(cart));
        navigate(getUri(`/checkout/confirmation`));
      } else {
        window.location.href = getUri('/checkout/payment');
      }
    } catch (error) {
      console.error('Error fetching order validation:', error);
      setLoading(false);
    }
  }

  function SubmitButton({
    onClick,
    className,
  }: {
    onClick: () => void;
    className?: string;
  }) {
    return (
      <ButtonWithArrow
        className={classNames(styles.submitButton, className)}
        onClick={() => {
          pushGtmEvent({
            event: '_click_other',
            category: 'clic autre - checkout',
            element: 'validation paiement',
          });

          onClick();
        }}
      >
        {t('SUBMIT_PAYMENT_BUTTON_PAY')}
      </ButtonWithArrow>
    );
  }

  function SummarySubmitButton(props: { onClick: () => void }) {
    return (
      <CheckoutSummaryAfter>
        <div className={styles.summaryButtonWrapper}>
          <SubmitButton {...props} />
        </div>
      </CheckoutSummaryAfter>
    );
  }

  function SubmitButtonFixed(props: { onClick: () => void }) {
    return createPortal(
      <SubmitButton {...props} className={styles.fixedSubmitButton} />,
      document.body
    );
  }

  return (
    <div className={styles.container}>
      <PaymentError className={styles.sectionPaymentError} />
      <Formik
        validateOnChange
        validateOnBlur={false}
        validateOnMount={false}
        enableReinitialize
        validationSchema={PaymentValidationSchema(t, paymentMethods, isUk)}
        onSubmit={onSubmit}
        initialValues={{
          paymentMethod: paymentMethod,
          cellPhone: customer?.cellPhone ?? '',
        }}
      >
        {({ handleSubmit }) => (
          <Form noValidate className={styles.form}>
            <FormikErrorsGtmLogger
              page_type="paiement"
              form_type="mon mode de paiement"
            />

            <PaymentMethods
              paymentMethods={paymentMethods}
              classes={{ container: styles.methods }}
              handleClick={handleClick}
              submitClicked={submitClicked}
              changeSubmitButtonVisibilities={setSubmitButtonVisibilities}
              setPaymentMethod={setPaymentMethod}
              cart={cart}
            />

            {submitClicked && <PaymentFieldsError />}
            {!isButtonHidden && (
              <SummarySubmitButton
                onClick={() => {
                  setSubmitClicked(true);
                  handleSubmit();
                }}
              />
            )}
            {!isButtonHidden && (
              <SubmitButtonFixed
                onClick={() => {
                  setSubmitClicked(true);
                  handleSubmit();
                }}
              />
            )}
            <ScrollToFieldError />
          </Form>
        )}
      </Formik>
      <ModalPageContent
        isOpen={open === 'pageContent'}
        closeModal={closeModal}
        pageUrl={targetUrl}
      />
      {isLoading && <Loading loading />}
    </div>
  );
}

export default PaymentContainer;
